/**
 * GameWorldView — the Phase 1 state seam.
 *
 * A single page-side snapshot function that returns a typed, stable read of
 * game state. It is the sanctioned way automation scenarios (ProbeRunner /
 * run-probe) read game state, replacing the bespoke `page.evaluate` blobs
 * scattered across scenarios.
 *
 * Design notes (mirror `__h2k_render_state.ts`):
 * - GATED at boot on `?testhooks=1` (or `?vqa=1`). When the flag is absent the
 *   global is never defined — zero footprint in prod.
 * - PULL MODEL: nothing runs per-frame. The snapshot is computed only when the
 *   test calls `window.__gameWorldView()`. No tick cost.
 * - Every source read is wrapped so a not-yet-ready subsystem yields a null/
 *   empty value rather than throwing — the hook must never break a live client.
 *
 * Field names are grounded against source (2026-06-29):
 * - player: `health`/`maxHealth` (Helix2000Character + server Player schema),
 *   NOT `hp`/`maxHp`.
 * - mob: `health` (Mob getter), `position` (getter → {x,y}) with x/y fallback.
 *
 * Blueprint: docs/automation/PHASE1_STATE_SEAM_BLUEPRINT.md
 */

import { gameLogger } from '../../utils/GameLogger';
import { ServiceRegistry } from '../core/ServiceRegistry';
import { bootSearchParams } from '../../config/boot/bootUrl';

export interface GameWorldView {
  kernelBooted: boolean;
  level: { id: string | null; name: string | null; width: number; height: number; platformCount: number };
  player: {
    exists: boolean;
    pos: { x: number; y: number } | null;
    health: number;
    maxHealth: number;
    level: number;
    xp: number;
    mana: number;
    peaceful: boolean;
    class: string | null;
  } | null;
  mobs: { count: number; nearest: { id: string; pos: { x: number; y: number }; health: number } | null };
  containers: { world: number; vfx: number; ui: number };
  recentEvents: string[];
}

declare global {
  interface Window {
    __gameWorldView?: () => GameWorldView;
  }
}

function isTestHooksEnabled(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const sp = bootSearchParams();
    return sp.get('testhooks') === '1' || sp.get('vqa') === '1';
  } catch {
    return false;
  }
}

/** Run fn; return fallback on any throw or undefined. The hook must never throw. */
function safe<T>(fn: () => T, fallback: T): T {
  try {
    const v = fn();
    return v === undefined ? fallback : v;
  } catch {
    return fallback;
  }
}

const EMPTY_LEVEL: GameWorldView['level'] = { id: null, name: null, width: 0, height: 0, platformCount: 0 };

function captureLevel(): GameWorldView['level'] {
  return safe(() => {
    const ms = ServiceRegistry.getInstance().get('MapSystem', { optional: true }) as
      | { getCurrentLevel?: () => any }
      | undefined;
    const lvl = ms?.getCurrentLevel?.();
    if (!lvl) return EMPTY_LEVEL;
    return {
      id: lvl.id ?? null,
      name: lvl.name ?? null,
      width: lvl.width ?? 0,
      height: lvl.height ?? 0,
      platformCount: (lvl.platforms?.length ?? 0),
    };
  }, EMPTY_LEVEL);
}

function capturePlayer(): GameWorldView['player'] {
  return safe(() => {
    const es = ServiceRegistry.getInstance().get('EntitySystem', { optional: true }) as
      | { getPlayerManager?: () => { getPlayer?: () => any } }
      | undefined;
    const p = es?.getPlayerManager?.()?.getPlayer?.();
    if (!p) return null;
    const pos = p.position ? { x: Math.round(p.position.x ?? 0), y: Math.round(p.position.y ?? 0) } : null;
    return {
      exists: true,
      pos,
      health: p.health ?? 0,
      maxHealth: p.maxHealth ?? 0,
      level: p.level ?? 0,
      xp: p.xp ?? 0,
      mana: p.mana ?? 0,
      peaceful: p.peaceful === true,
      class: p.class ?? null,
    };
  }, null);
}

function captureMobs(playerPos: { x: number; y: number } | null): GameWorldView['mobs'] {
  return safe(() => {
    const mobSys = ServiceRegistry.getInstance().get('MobSystem', { optional: true }) as
      | { getManager?: () => { getAllMobs?: () => any[] } | null }
      | undefined;
    const mgr = mobSys?.getManager?.();
    const mobs = mgr?.getAllMobs?.() ?? [];
    if (mobs.length === 0) return { count: 0, nearest: null };
    const px = playerPos?.x ?? 0;
    let best: any = null;
    let bd = Infinity;
    for (const m of mobs) {
      const mx = m.position?.x ?? m.x ?? 0;
      const my = m.position?.y ?? m.y ?? 0;
      const d = Math.abs(mx - px);
      if (d < bd) {
        bd = d;
        best = { id: m.id, pos: { x: Math.round(mx), y: Math.round(my) }, health: m.health ?? 0 };
      }
    }
    return { count: mobs.length, nearest: best };
  }, { count: 0, nearest: null });
}

function captureContainers(): GameWorldView['containers'] {
  return safe(() => {
    const ph = (window as any).__PIXI_OVERLAY_HOST__ as
      | { worldContainer?: { children?: { length: number } | any[] }; vfxContainer?: { children?: { length: number } | any[] }; uiContainer?: { children?: { length: number } | any[] } }
      | undefined;
    const len = (c?: { children?: { length: number } | any[] }): number => {
      if (!c?.children) return 0;
      return typeof c.children.length === 'number' ? c.children.length : 0;
    };
    return { world: len(ph?.worldContainer), vfx: len(ph?.vfxContainer), ui: len(ph?.uiContainer) };
  }, { world: 0, vfx: 0, ui: 0 });
}

function captureKernelBooted(): boolean {
  return safe(() => {
    const scene = (window as any).__currentScene;
    return !!(scene?.setup?.kernel?.getIsBooted?.());
  }, false);
}

function captureRecentEvents(): string[] {
  return safe(() => {
    const bus = (window as any).__EVENT_BUS__;
    if (!bus?.getEventStats) return [];
    const stats = bus.getEventStats() as Array<{ name: string; emitsLastSec: number }>;
    return stats.filter((s) => s.emitsLastSec > 0).map((s) => s.name);
  }, []);
}

/** The snapshot — pure read aggregator. No mutation, no per-frame cost. */
export function snapshotGameWorldView(): GameWorldView {
  const player = capturePlayer();
  return {
    kernelBooted: captureKernelBooted(),
    level: captureLevel(),
    player,
    mobs: captureMobs(player?.pos ?? null),
    containers: captureContainers(),
    recentEvents: captureRecentEvents(),
  };
}

let _installed = false;

/** Test-only reset so the install gate can be re-tested in the same file. */
export function __resetGameWorldViewForTests(): void {
  _installed = false;
  try { delete (window as any).__gameWorldView; } catch { /* ignore */ }
}

/**
 * Install `window.__gameWorldView` when `?testhooks=1` (or `?vqa=1`).
 * Sibling of `installRenderStateHook` — installed alongside it in main.tsx.
 */
export function installGameWorldViewHook(): void {
  if (!isTestHooksEnabled()) return;
  if (typeof window === 'undefined') return;
  if (_installed) return;
  window.__gameWorldView = snapshotGameWorldView;
  _installed = true;
  gameLogger.info('boot', '[GameWorldView] installed — window.__gameWorldView() exposed via ?testhooks=1 (or ?vqa=1)');
}
